import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Fade } from 'react-awesome-reveal';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import makingSushi from '../../assets/photos/making_sushi.jpg';
import sushi1 from '../../assets/photos/sushi1.jpg';
import comida2 from '../../assets/photos/comida2.webp';
import comida4 from '../../assets/photos/comida4.webp';
import comida5 from '../../assets/photos/comida5.webp';
import sushi3 from '../../assets/photos/comida_terra.webp';

const PHOTOS = [
  {
    src: makingSushi,
    label: 'Sushiman en directo',
    gridColumn: { xs: '1 / -1', md: '1 / 9' },
    gridRow: { md: 'span 2' },
    height: { xs: 260, md: 508 },
  },
  {
    src: sushi1,
    label: 'Selección de nigiri',
    gridColumn: { xs: 'span 6', md: '9 / 13' },
    height: { xs: 190, md: 246 },
  },
  {
    src: comida2,
    label: 'Rolls premium',
    gridColumn: { xs: 'span 6', md: '9 / 13' },
    height: { xs: 190, md: 246 },
  },
  {
    src: comida4,
    label: 'Creaciones del día',
    gridColumn: { xs: 'span 4', md: '1 / 5' },
    height: { xs: 160, md: 290 },
  },
  {
    src: comida5,
    label: 'Presentación a medida',
    gridColumn: { xs: 'span 4', md: '5 / 9' },
    height: { xs: 160, md: 290 },
  },
  {
    src: sushi3,
    label: 'Experiencia Sushi VIP',
    gridColumn: { xs: 'span 4', md: '9 / 13' },
    height: { xs: 160, md: 290 },
  },
];

const Gallery: React.FC = () => {
  const [selected, setSelected] = useState<number | null>(null);

  const close = useCallback(() => setSelected(null), []);
  const prev = useCallback(() => setSelected((s) => (s === null ? 0 : (s - 1 + PHOTOS.length) % PHOTOS.length)), []);
  const next = useCallback(() => setSelected((s) => (s === null ? 0 : (s + 1) % PHOTOS.length)), []);

  useEffect(() => {
    if (selected === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selected, close, prev, next]);

  return (
    <Box id="gallery" sx={{ py: 10, background: 'var(--color-bg-section)' }}>
      <Container maxWidth="lg">
        <Fade triggerOnce direction="up">
          <Typography
            variant="overline"
            align="center"
            sx={{ display: 'block', color: 'var(--color-accent)', letterSpacing: '0.3em', mb: 1 }}
          >
            Galería
          </Typography>
          <Typography
            variant="h2"
            align="center"
            sx={{ fontFamily: 'var(--font-heading)', fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 2 }}
          >
            Momentos Sushigi
          </Typography>
          <Typography align="center" sx={{ color: 'var(--color-text-secondary)', maxWidth: 520, mx: 'auto', mb: 8 }}>
            Cada evento es único. Así lo son también las piezas que elaboramos en él.
          </Typography>
        </Fade>

        {/* Photo grid — Box is the direct grid child so gridColumn/gridRow take effect */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: { xs: 1, md: 1.5 },
          }}
        >
          {PHOTOS.map((photo, i) => (
            <Box
              key={i}
              component={motion.div}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.55, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
              sx={{
                gridColumn: photo.gridColumn,
                gridRow: photo.gridRow,
                height: photo.height,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 1.5,
                cursor: 'zoom-in',
                bgcolor: 'var(--color-bg-card)',
                '&:hover .photo-img': { transform: 'scale(1.06)' },
                '&:hover .photo-overlay': { opacity: 1 },
              }}
              onClick={() => setSelected(i)}
            >
              <Box
                className="photo-img"
                component="img"
                src={photo.src}
                alt={photo.label}
                loading="lazy"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                }}
              />
              <Box
                className="photo-overlay"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 2.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'white', fontWeight: 500, letterSpacing: '0.04em' }}
                >
                  {photo.label}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Lightbox */}
      <AnimatePresence>
        {selected !== null && (
          <motion.div
            key="lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1400,
              background: 'rgba(0,0,0,0.93)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <motion.img
              key={selected}
              src={PHOTOS[selected].src}
              alt={PHOTOS[selected].label}
              initial={{ scale: 0.88, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '88vw',
                maxHeight: '82vh',
                objectFit: 'contain',
                borderRadius: 8,
                boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
                userSelect: 'none',
              }}
            />

            <Box sx={{ position: 'fixed', bottom: { xs: 20, md: 32 }, left: '50%', transform: 'translateX(-50%)', textAlign: 'center', pointerEvents: 'none' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.06em' }}>
                {PHOTOS[selected].label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                {selected + 1} / {PHOTOS.length}
              </Typography>
            </Box>

            <IconButton onClick={close} aria-label="Cerrar galería" sx={{ position: 'fixed', top: { xs: 16, md: 24 }, right: { xs: 16, md: 24 }, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <CloseIcon />
            </IconButton>

            <IconButton onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Foto anterior" sx={{ position: 'fixed', left: { xs: 8, md: 24 }, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(200,169,110,0.3)' } }}>
              <ArrowBackIosNewIcon />
            </IconButton>

            <IconButton onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Foto siguiente" sx={{ position: 'fixed', right: { xs: 8, md: 24 }, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(200,169,110,0.3)' } }}>
              <ArrowForwardIosIcon />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Gallery;
